import React, { useState, useEffect } from 'react';
import { Star, ThumbsUp, User, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Star Rating Component
function StarRating({ rating, onRatingChange, readonly = false, size = 'md' }) {
    const [hoverRating, setHoverRating] = useState(0);
    const sizeClasses = {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-6 w-6'
    };

    return (
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    disabled={readonly}
                    onClick={() => !readonly && onRatingChange?.(star)}
                    onMouseEnter={() => !readonly && setHoverRating(star)}
                    onMouseLeave={() => !readonly && setHoverRating(0)}
                    className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-colors`}
                >
                    <Star
                        className={`${sizeClasses[size]} ${
                            star <= (hoverRating || rating)
                                ? 'fill-amber-400 text-amber-400'
                                : 'text-gray-300'
                        }`}
                    />
                </button>
            ))}
        </div>
    );
}

// Review Form Component
function ReviewForm({ productId, onReviewSubmitted }) {
    const { user } = useAuth();
    const [rating, setRating] = useState(0);
    const [title, setTitle] = useState('');
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!user) {
            toast.error('Connectez-vous pour laisser un avis');
            return;
        }

        if (rating === 0) {
            toast.error('Veuillez sélectionner une note');
            return;
        }

        if (title.length < 3) {
            toast.error('Le titre doit contenir au moins 3 caractères');
            return;
        }

        if (comment.length < 10) {
            toast.error('Le commentaire doit contenir au moins 10 caractères');
            return;
        }

        setSubmitting(true);
        try {
            await apiRequest('/reviews', {
                method: 'POST',
                body: JSON.stringify({
                    product_id: productId,
                    rating,
                    title,
                    comment
                })
            });
            toast.success('Avis publié avec succès !');
            setRating(0);
            setTitle('');
            setComment('');
            onReviewSubmitted?.();
        } catch (error) {
            toast.error(error.message || 'Erreur lors de la publication');
        } finally {
            setSubmitting(false);
        }
    };

    if (!user) {
        return (
            <Card className="bg-muted/50">
                <CardContent className="py-6 text-center">
                    <p className="text-muted-foreground">
                        Connectez-vous pour laisser un avis sur ce produit
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg">Donner votre avis</CardTitle>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label>Votre note</Label>
                        <div className="mt-2">
                            <StarRating rating={rating} onRatingChange={setRating} size="lg" />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="review-title">Titre de l'avis</Label>
                        <Input
                            id="review-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Résumez votre expérience"
                            maxLength={100}
                            className="mt-1"
                        />
                    </div>

                    <div>
                        <Label htmlFor="review-comment">Votre commentaire</Label>
                        <Textarea
                            id="review-comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Partagez votre expérience avec ce produit..."
                            rows={4}
                            maxLength={1000}
                            className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            {comment.length}/1000 caractères
                        </p>
                    </div>

                    <Button type="submit" disabled={submitting} className="w-full">
                        {submitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Publication...
                            </>
                        ) : (
                            'Publier mon avis'
                        )}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
}

// Single Review Component
function ReviewItem({ review, onHelpful }) {
    const { user } = useAuth();
    const [voting, setVoting] = useState(false);

    const handleHelpful = async () => {
        if (!user) {
            toast.error('Connectez-vous pour voter');
            return;
        }

        setVoting(true);
        try {
            await apiRequest(`/reviews/${review.review_id}/helpful`, {
                method: 'POST',
                body: JSON.stringify({ helpful: true })
            });
            toast.success('Vote enregistré');
            onHelpful?.();
        } catch (error) {
            toast.error(error.message || 'Erreur lors du vote');
        } finally {
            setVoting(false);
        }
    };

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    return (
        <div className="border-b last:border-0 py-4">
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        <StarRating rating={review.rating} readonly size="sm" />
                        {review.verified_purchase && (
                            <Badge variant="secondary" className="text-xs">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Achat vérifié
                            </Badge>
                        )}
                    </div>
                    <h4 className="font-semibold">{review.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{review.comment}</p>
                    
                    <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {review.user_name}
                        </div>
                        <span>•</span>
                        <span>{formatDate(review.created_at)}</span>
                    </div>
                </div>
            </div>
            
            <div className="mt-3 flex items-center gap-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleHelpful}
                    disabled={voting}
                    className="text-muted-foreground hover:text-foreground"
                >
                    <ThumbsUp className="h-4 w-4 mr-1" />
                    Utile ({review.helpful_count || 0})
                </Button>
            </div>
        </div>
    );
}

// Rating Distribution Component
function RatingDistribution({ stats }) {
    const total = stats.total_reviews || 1;
    
    return (
        <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((stars) => {
                const count = stats.rating_distribution?.[stars] || 0;
                const percentage = (count / total) * 100;
                
                return (
                    <div key={stars} className="flex items-center gap-2 text-sm">
                        <span className="w-8">{stars}</span>
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        <Progress value={percentage} className="h-2 flex-1" />
                        <span className="w-8 text-muted-foreground">{count}</span>
                    </div>
                );
            })}
        </div>
    );
}

// Main ProductReviews Component
export default function ProductReviews({ productId }) {
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadReviews = async () => {
        try {
            const data = await apiRequest(`/reviews/product/${productId}`);
            setReviews(data.reviews || []);
            setStats(data.stats || null);
        } catch (error) {
            console.error('Error loading reviews:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReviews();
    }, [productId]);

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="product-reviews">
            {/* Stats Summary */}
            {stats && stats.total_reviews > 0 && (
                <Card>
                    <CardContent className="py-6">
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="text-center md:text-left">
                                <div className="text-5xl font-bold text-primary">
                                    {stats.average_rating}
                                </div>
                                <div className="mt-2">
                                    <StarRating rating={Math.round(stats.average_rating)} readonly />
                                </div>
                                <p className="text-muted-foreground mt-1">
                                    Basé sur {stats.total_reviews} avis
                                </p>
                            </div>
                            <RatingDistribution stats={stats} />
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Review Form */}
            <ReviewForm productId={productId} onReviewSubmitted={loadReviews} />

            {/* Reviews List */}
            <div>
                <h3 className="text-lg font-semibold mb-4">
                    Avis clients ({reviews.length})
                </h3>
                
                {reviews.length === 0 ? (
                    <Card className="bg-muted/50">
                        <CardContent className="py-8 text-center">
                            <p className="text-muted-foreground">
                                Aucun avis pour ce produit. Soyez le premier à donner votre avis !
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <CardContent className="py-2">
                            {reviews.map((review) => (
                                <ReviewItem 
                                    key={review.review_id} 
                                    review={review}
                                    onHelpful={loadReviews}
                                />
                            ))}
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}

export { StarRating };
